import clsx from 'clsx';
import React, { ReactNode, useRef, useContext } from 'react';
import { useContextSelector } from 'use-context-selector';
import { isNil } from 'ramda';

import { useKeyPress } from 'src/hooks/useKeyPress';

import { ModalContext } from 'src/contexts/modal.context';
import { ResizableWindowContext } from 'src/contexts/resizableWindow.context';

import { H6 } from '../Typography';
import { PlainButton, PrimaryButton, CloseButton } from '../Button';
import { mockFn } from 'src/tools/fn.tools';
import { ifTrue } from 'src/tools/logic.tools';
import { ErrorMessage } from '../ErrorMessage';
import { useScrollShadows } from 'src/hooks/useScrollShadows';

import classes from './ModalDialog.module.scss';

type Button = [label: string, onClick: (() => void) | 'default' | 'none', isDisabled?: boolean];

interface Props {
  bottomRightContent?: ReactNode; // dedicated bottom row
  bottomError?: string; // show error in the bottom
  children: ReactNode;
  containerClassName?: string; // class for the dialog
  contentClassName?: string; // class for content only
  primaryButton?: Button; // primary button details
  secondaryButton?: Button; // secondary button details
  title?: string;
  isWithTabs?: boolean;
}

// new-ui
export function ModalDialog({
  containerClassName,
  contentClassName,
  primaryButton,
  secondaryButton,
  bottomRightContent,
  title,
  bottomError,
  children,
  isWithTabs,
}: Props) {
  const closeModal = useContextSelector(ModalContext, c => c.closeModal);

  const { closeWindow } = useContext(ResizableWindowContext);

  const renderConfirmation = useContextSelector(ModalContext, c => c.renderConfirmation);

  const isHideCloseButton = useContextSelector(ModalContext, c => c.isHideCloseButton);

  function handleClose(e?: KeyboardEvent) {
    if (closeModal) {
      e?.stopPropagation();
      closeModal();
    } else if (closeWindow) {
      e?.stopPropagation();
      closeWindow();
    }
  }

  const scrollableRef = useRef<HTMLDivElement>(null);

  const [isShowTopShadow, isShowBottomShadow] = useScrollShadows(scrollableRef);

  useKeyPress({ onEscape: handleClose });

  const primaryOrSecondaryPresent = () => !!primaryButton || !!secondaryButton;

  function primaryButtonFn() {
    if (!primaryButton || typeof primaryButton[1] === 'string') return mockFn;

    return primaryButton[1];
  }

  function secondaryButtonFn() {
    if (!secondaryButton || secondaryButton[1] === 'none') return mockFn;

    if (secondaryButton[1] === 'default') return handleClose;

    return secondaryButton[1];
  }

  function renderPrimaryButton() {
    if (!primaryButton) return <></>;

    return (
      <PrimaryButton label={primaryButton![0]} isDisabled={Boolean(primaryButton[2])} onClick={primaryButtonFn()} />
    );
  }

  function renderSecondaryButton() {
    if (!secondaryButton) return <></>;

    return (
      <PlainButton label={secondaryButton[0]} isDisabled={Boolean(secondaryButton[2])} onClick={secondaryButtonFn()} />
    );
  }

  function renderError() {
    if (!bottomError) return;

    return <ErrorMessage className={clsx(classes.error, 'f-1 o-hidden')} text={bottomError} />;
  }

  function renderFooter() {
    if (primaryOrSecondaryPresent())
      return (
        <footer
          className={clsx(classes.footer, 'row w-100p j-between rnd-header-draggable', {
            'bottom-scroll-shadow': isShowBottomShadow,
          })}
        >
          <div className='row'>
            {!!secondaryButton && renderSecondaryButton()}
            {!!primaryButton && !!secondaryButton && <div className='spacer-horizontal-2r' />}
            {!!primaryButton && renderPrimaryButton()}
          </div>
          {ifTrue(bottomError, renderError)}
          {ifTrue(bottomRightContent, <div className={clsx(classes.right, 'row a-center')}>{bottomRightContent}</div>)}
        </footer>
      );
  }

  function handleOnCloseClick() {
    if (closeWindow) closeWindow();
    else closeModal();
  }

  function renderCloseButton() {
    return <CloseButton className={classes.close} onClick={handleOnCloseClick} />;
  }

  function renderTitle() {
    return <H6 className='t-ellipsis f-1'>{title}</H6>;
  }

  return (
    <div
      data-testid='modal-dialog'
      className={clsx('card-item column o-hidden', classes.container, containerClassName, {
        [classes['with-tabs']]: isWithTabs,
      })}
    >
      <header
        className={clsx('row items-p8 rnd-header-draggable', classes.header, {
          'top-scroll-shadow': isShowTopShadow,
          [classes['without-title']]: !title,
        })}
      >
        {ifTrue(title, renderTitle)}
        {ifTrue(!isNil(closeWindow || closeModal) && !isHideCloseButton, renderCloseButton)}
      </header>
      <div ref={scrollableRef} className={clsx('column w-100p f-1 scroll-bar', classes.scrollable)}>
        <main className={clsx(classes.content, 'w-100p f-1', contentClassName, ifTrue(isWithTabs, 'column o-hidden'))}>
          {children}
        </main>
        {renderFooter()}
      </div>
      {renderConfirmation?.()}
    </div>
  );
}
